/**
 * GlobalErrorBoundary — Internal Sentry alternative (Iter-279)
 * =============================================================
 * Catches uncaught React render errors, ships them to the Corvana internal
 * error bus at `POST /api/ops/errors`, and shows a minimal recovery UI.
 *
 * Why internal instead of Sentry:
 *   • AU data residency (APP 8 compliance) — no cross-border stack traces.
 *   • Re-uses the existing Mongo-backed `error_bus` service + Slack Ops
 *     Alerter for new-issue notifications.
 *   • Zero vendor cost / lock-in.
 *
 * Usage: wrap the App root in `<GlobalErrorBoundary>`.
 */

import React from 'react';

const ERROR_ENDPOINT = '/api/ops/errors';

export class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, fingerprint: null, resetKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Fire-and-forget POST. Never let telemetry break the recovery UI.
    this.shipToErrorBus(error, info).catch(() => {
      // Swallow — we've already entered the error recovery render path.
    });
    // Proactive support: also log a customer-facing signal so the crash shows
    // up in the Tech Panel + the customer gets a "we're on it" acknowledgement.
    import('@/utils/clientTelemetry')
      .then((m) => m.reportCrash(error, info))
      .catch(() => {});
  }

  async shipToErrorBus(error, info) {
    try {
      const userInfo = safeParseJSON(localStorage.getItem('corvana_user')) || {};
      const body = {
        message: String(error?.message || error || 'Unknown React error'),
        stack: (info?.componentStack || error?.stack || '').slice(0, 8000),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_id: userInfo?.id || undefined,
        business_id: userInfo?.business_id || undefined,
        context: { user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined },
      };

      const res = await fetch(`${window.location.origin}${ERROR_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Don't send credentials — the error bus POST endpoint is intentionally
        // open + rate-limited, and we don't want a broken auth context to
        // prevent error reporting.
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        this.setState({ fingerprint: data?.fingerprint || null });
      }
    } catch (_) {
      // silent
    }
  }

  handleTryAgain = () => {
    // Recover IN PLACE — clear the error and remount the subtree (bump resetKey)
    // without a full reload, so the user keeps their session and where they were.
    this.setState((s) => ({ hasError: false, fingerprint: null, resetKey: s.resetKey + 1 }));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    // "Go to dashboard" — route into the authenticated app, not the public
    // marketing homepage (which is what `/` renders).
    window.location.href = '/app/dashboard';
  };

  render() {
    if (!this.state.hasError) {
      // Keyed so `handleTryAgain` remounts the subtree cleanly after a recovery.
      return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }

    return (
      <div
        data-testid="global-error-boundary"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0a0a0b',
          color: '#e7e7ea',
          fontFamily: '"Inter", system-ui, sans-serif',
          padding: '2rem',
        }}
      >
        <div style={{ maxWidth: 560, textAlign: 'left' }}>
          <div style={{ fontSize: '0.78rem', letterSpacing: '0.2em', opacity: 0.55, textTransform: 'uppercase' }}>
            Corvana
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 600, marginTop: '0.25rem', marginBottom: '1rem' }}>
            Something broke on this page.
          </h1>
          <p style={{ opacity: 0.75, marginBottom: '0.5rem' }}>
            We've logged the issue automatically — no need to email support, our engineers already see it.
            Tap <strong>Try again</strong> to recover without losing your place.
          </p>
          {this.state.fingerprint ? (
            <p style={{ fontSize: '0.78rem', opacity: 0.4, marginBottom: '2rem' }}>
              Reference: <code>{this.state.fingerprint}</code>
            </p>
          ) : (
            <div style={{ marginBottom: '2rem' }} />
          )}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              data-testid="error-boundary-try-again-btn"
              onClick={this.handleTryAgain}
              style={{
                padding: '0.65rem 1.2rem',
                borderRadius: 999,
                border: '1px solid #2b2b30',
                background: '#e7e7ea',
                color: '#0a0a0b',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              style={{
                padding: '0.65rem 1.2rem',
                borderRadius: 999,
                border: '1px solid #2b2b30',
                background: 'transparent',
                color: '#e7e7ea',
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
            <button
              data-testid="error-boundary-home-btn"
              onClick={this.handleGoHome}
              style={{
                padding: '0.65rem 1.2rem',
                borderRadius: 999,
                border: '1px solid #2b2b30',
                background: 'transparent',
                color: '#e7e7ea',
                cursor: 'pointer',
              }}
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function safeParseJSON(s) {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export default GlobalErrorBoundary;
